import { FC, Fragment, MouseEventHandler } from "react";

import {
  faFilter,
  faChartSimple,
  faEllipsisVertical,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  PlusIcon,
} from "@heroicons/react/20/solid";
import { t } from "i18next";
import { ButtonSecondary } from "../atoms/ButtonSecondary";
import { DateTime } from "luxon";

export type HeaderCalendarprops = {
  currentDateTime: DateTime;
  onToday: MouseEventHandler;
  onNext: MouseEventHandler;
  onPrev: MouseEventHandler;
  onViewTypeChange: (viewType: "day" | "week" | "month") => void;
  viewType: "day" | "week" | "month";
  hideFilter: boolean;

  filtersOpen?: () => void;
  functionButtonTopCreate?: () => void;
  functionButtonTime?: () => void;
  functionButtonCalendar?: () => void;
  functionOpenModalFestivos?: () => void;
  functionOpenModalSala?: () => void;
  functionOpenModalGrupos?: () => void;
  functionOpenModalBonos?: () => void;
  funcionOpenKpis?: () => void;
  canCreateActivity?: boolean;
  canEditTimesActivity?: boolean;
  canUploadCalendar?: boolean;
  canEditHolidaysActivity?: boolean;
  canSeeKpis?: boolean;
};

export const HeaderCalendar: FC<HeaderCalendarprops> = ({
  onToday,
  onNext,
  onPrev,
  onViewTypeChange,
  viewType,
  currentDateTime,
  hideFilter,
  filtersOpen,
  functionButtonTopCreate,
  functionButtonTime,
  functionButtonCalendar,
  functionOpenModalFestivos,
  functionOpenModalSala,
  functionOpenModalGrupos,
  functionOpenModalBonos,
  funcionOpenKpis,
  canCreateActivity,
  canEditTimesActivity,
  canUploadCalendar,
  canEditHolidaysActivity,
  canSeeKpis,
}) => {
  if (!currentDateTime) {
    return null; // O maneja el caso en que currentDateTime sea undefined
  }

  return (
    <header className="flex items-center justify-between border-b border-gray-200 px-6 py-4 lg:flex-none">
      <h1 className="-ml-3 mr-3  text-sm sm:text-base font-semibold leading-6 text-gray-900">
        <time dateTime={currentDateTime.toISO()?.toString()}>
          {currentDateTime
            .setLocale(localStorage.getItem("i18nextLng"))
            .toFormat("MMMM yyyy")}
        </time>
      </h1>

      <div className="flex items-center sm:ml-3 md:ml-8 lg:ml-8 xl:ml-8 2xl:ml-8">
        {!hideFilter && (
          <Fragment>
            <div className="ml-10 hidden md:flex items-center">
              <button
                type="button"
                className="sm:ml-2 p-2 text-gray-400 hover:text-gray-500"
                onClick={filtersOpen}
              >
                <span className="sr-only"></span>
                <FontAwesomeIcon
                  icon={faFilter}
                  aria-hidden="true"
                  className="h-6 w-6"
                />
              </button>
              {funcionOpenKpis && canSeeKpis && (
                <button
                  type="button"
                  className="p-2 mr-3 text-gray-400 hover:text-gray-500"
                  onClick={funcionOpenKpis}
                >
                  <span className="sr-only"></span>
                  <FontAwesomeIcon
                    icon={faChartSimple}
                    aria-hidden="true"
                    className="h-6 w-6"
                  />
                </button>
              )}
              {functionOpenModalBonos && (
                <Fragment>
                  <div className="mr-6 h-6 w-px bg-gray-300" />
                  <div className="mr-6">
                    <ButtonSecondary
                      text={t("lesson.views.bond")}
                      onClick={functionOpenModalBonos}
                      size="large"
                    />
                  </div>
                </Fragment>
              )}
              <div className="mr-6 h-6 w-px bg-gray-300" />
              {functionOpenModalSala && (
                <div className="mr-6">
                  <ButtonSecondary
                    text={t("lesson.views.rooms")}
                    onClick={functionOpenModalSala}
                    size="large"
                  />
                </div>
              )}
              <ButtonSecondary
                text={t("lesson.views.groups")}
                onClick={functionOpenModalGrupos}
                size="large"
              />
              <div className="ml-6 mr-6 h-6 w-px bg-gray-300" />
            </div>
          </Fragment>
        )}
        <div className="relative flex items-center rounded-md bg-white shadow-sm lg:items-stretch">
          <button
            type="button"
            className="flex h-8 w-10 items-center justify-center rounded-l-md border-y border-l border-gray-300 pr-1 text-gray-400 hover:text-gray-500 focus:relative sm:h-9 sm:w-12 lg:w-9 lg:pr-0 lg:hover:bg-gray-50"
            onClick={onPrev}
          >
            <span className="sr-only">{t("lesson.views.month_previous")}</span>
            <ChevronLeftIcon
              className="h-4 w-4 sm:h-5 sm:w-5"
              aria-hidden="true"
            />
          </button>
          <button
            onClick={onToday}
            type="button"
            className="hidden border-y border-gray-300 px-2 text-xs font-semibold text-gray-900 hover:bg-gray-50 focus:relative sm:px-3.5 sm:text-sm lg:block"
          >
            {t("lesson.views.today")}
          </button>
          <span className="relative -mx-px h-5 w-px bg-gray-300 lg:hidden" />
          <button
            onClick={onNext}
            type="button"
            className="flex h-8 w-10 items-center justify-center rounded-r-md border-y border-r border-gray-300 pl-1 text-gray-400 hover:text-gray-500 focus:relative sm:h-9 sm:w-12 lg:w-9 lg:pl-0 lg:hover:bg-gray-50"
          >
            <span className="sr-only">{t("lesson.views.month_next")}</span>
            <ChevronRightIcon
              className="h-4 w-4 sm:h-5 sm:w-5"
              aria-hidden="true"
            />
          </button>
        </div>
        <div className="hidden md:ml-4 md:flex md:items-center">
          {/* Dropdown Dia-Semana-Mes */}
          <Menu as="div" className="relative">
            <MenuButton
              type="button"
              className="flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              {viewType === "day" ? (
                <>{t("lesson.views.day")}</>
              ) : (
                <>
                  {viewType === "week" ? (
                    <>{t("lesson.views.week")}</>
                  ) : (
                    <>
                      {viewType === "month" ? (
                        <>{t("lesson.views.month")}</>
                      ) : (
                        <></>
                      )}
                    </>
                  )}
                </>
              )}

              <ChevronDownIcon
                className="-mr-1 h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </MenuButton>

            <MenuItems
              transition
              className="absolute right-0 z-10 mt-3 w-36 origin-top-right overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
            >
              <div className="py-1">
                <MenuItem>
                  <a
                    href="#"
                    onClick={() => {
                      onViewTypeChange("day");
                    }}
                    className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                  >
                    {t("lesson.views.day")}
                  </a>
                </MenuItem>
                <MenuItem>
                  <a
                    href="#"
                    onClick={() => {
                      onViewTypeChange("week");
                    }}
                    className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                  >
                    {t("lesson.views.week")}
                  </a>
                </MenuItem>
                <MenuItem>
                  <a
                    href="#"
                    onClick={() => {
                      onViewTypeChange("month");
                    }}
                    className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                  >
                    {t("lesson.views.month")}
                  </a>
                </MenuItem>
              </div>
            </MenuItems>
          </Menu>
          {/*Fin Dropdown Dia-Semana-Mes */}
          {/*MENU TRES PUNTOS ACCIONES VARIAS*/}
          {functionButtonTime && (
            <Menu as="div" className="relative ml-6">
              <MenuButton className="-mx-2 flex items-center rounded-full border border-transparent p-2 text-gray-400 hover:text-gray-500">
                <span className="sr-only"></span>
                <FontAwesomeIcon
                  icon={faEllipsisVertical}
                  aria-hidden="true"
                  className="h-5 w-5"
                />
              </MenuButton>
              <MenuItems
                transition
                className="absolute right-0 z-10 mt-3 w-36 origin-top-right divide-y divide-gray-100 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
              >
                {canEditTimesActivity && (
                  <div className="py-1">
                    <MenuItem>
                      <a
                        onClick={functionButtonTime}
                        href="#"
                        className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                      >
                        {t("lesson.views.times")}
                      </a>
                    </MenuItem>
                  </div>
                )}

                {canEditHolidaysActivity && (
                  <div className="py-1">
                    <MenuItem>
                      <a
                        onClick={functionOpenModalFestivos}
                        href="#"
                        className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                      >
                        {t("lesson.views.festivos")}
                      </a>
                    </MenuItem>
                  </div>
                )}

                {functionButtonCalendar && canUploadCalendar && (
                  <div className="py-1">
                    <MenuItem>
                      <a
                        onClick={functionButtonCalendar}
                        href="#"
                        className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                      >
                        {t("lesson.views.up_calendar")}
                      </a>
                    </MenuItem>
                  </div>
                )}
              </MenuItems>
            </Menu>
          )}
          {/*FIN MENU TRES PUNTOS */}
          {/*FUNCTION BUTTON TOP CREATE */}
          {functionButtonTopCreate && canCreateActivity && (
            <>
              <div className="ml-6 h-6 w-px bg-gray-300" />
              <button
                onClick={functionButtonTopCreate}
                type="button"
                className="ml-6 mr-3 rounded-full border-2 border-dashed border-gray-400 p-1 text-gray-400 shadow-sm hover:text-gray-500 focus-visible:outline-none"
              >
                <PlusIcon aria-hidden="true" className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
        {/*FIN FUNCTION BUTTON TOP CREATE */}
        {/*MENU TRES PUNTOS PANTALLAS PEQUEÑAS */}
        <Menu
          as="div"
          className=" relative ml-2 sm:ml-6 md:ml-6 lg:ml-6 xl:ml-6 2xl:ml-6 md:hidden"
        >
          <MenuButton className="-mx-2 flex items-center rounded-full border border-transparent p-2 text-gray-400 hover:text-gray-500">
            <span className="sr-only"></span>
            <FontAwesomeIcon
              icon={faEllipsisVertical}
              aria-hidden="true"
              className="h-5 w-5"
            />
          </MenuButton>
          <MenuItems
            transition
            className="absolute right-0 z-10 mt-3 w-36 origin-top-right divide-y divide-gray-100 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
          >
            <div className="py-1">
              <MenuItem>
                <button
                  onClick={() => {}}
                  type="button"
                  className="ml-4 mt-1 mb-1 rounded-full border-2 border-dashed border-gray-400 p-1 text-gray-400 shadow-sm hover:text-gray-500 focus-visible:outline-none"
                >
                  <PlusIcon aria-hidden="true" className="h-5 w-5" />
                </button>
              </MenuItem>
            </div>
            <div className="py-1">
              <MenuItem>
                <a
                  href="#"
                  className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                >
                  {t("lesson.views.today")}
                </a>
              </MenuItem>
            </div>
            <div className="py-1">
              <MenuItem>
                <a
                  href="#"
                  className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                >
                  {t("lesson.views.day")}
                </a>
              </MenuItem>
              <MenuItem>
                <a
                  href="#"
                  className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                >
                  {t("lesson.views.week")}
                </a>
              </MenuItem>
              <MenuItem>
                <a
                  href="#"
                  className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                >
                  {t("lesson.views.month")}
                </a>
              </MenuItem>
            </div>
            <div className="py-1">
              <MenuItem>
                <a
                  onClick={functionButtonTime}
                  href="#"
                  className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                >
                  {t("lesson.views.times")}
                </a>
              </MenuItem>
            </div>
            <div className="py-1">
              <MenuItem>
                <a
                  onClick={functionOpenModalFestivos}
                  href="#"
                  className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                >
                  {t("lesson.views.festivos")}
                </a>
              </MenuItem>
            </div>
            <div className="py-1">
              <MenuItem>
                <a
                  onClick={functionButtonCalendar}
                  href="#"
                  className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                >
                  {t("lesson.views.up_calendar")}
                </a>
              </MenuItem>
            </div>
            <div className="py-1">
              <MenuItem>
                <a
                  onClick={functionOpenModalBonos}
                  href="#"
                  className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                >
                  {t("lesson.views.bond")}
                </a>
              </MenuItem>
            </div>
            <div className="py-1">
              <MenuItem>
                <a
                  onClick={functionOpenModalSala}
                  href="#"
                  className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                >
                  {t("lesson.views.rooms")}
                </a>
              </MenuItem>
              <MenuItem>
                <a
                  onClick={functionOpenModalGrupos}
                  href="#"
                  className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                >
                  {t("lesson.views.groups")}
                </a>
              </MenuItem>
            </div>
            <div className="py-1">
              <MenuItem>
                <a
                  href="#"
                  className="block px-4 py-2 text-sm text-gray-400"
                  onClick={filtersOpen}
                >
                  <FontAwesomeIcon
                    icon={faFilter}
                    aria-hidden="true"
                    className="h-6 w-6 hover:text-gray-500"
                  />
                </a>
              </MenuItem>
              <MenuItem>
                <a
                  onClick={funcionOpenKpis}
                  href="#"
                  className="block px-4 py-2 text-sm text-gray-400"
                >
                  <FontAwesomeIcon
                    icon={faChartSimple}
                    aria-hidden="true"
                    className="h-6 w-6 hover:text-gray-500"
                  />
                </a>
              </MenuItem>
            </div>
          </MenuItems>
        </Menu>
        {/*FIN MENU TRES PUNTOS PANTALLAS PEQUEÑAS */}
      </div>
    </header>
  );
};
